import { GridApi } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ZodArray, ZodType } from 'zod';

import { useGridValidator } from '../../components/IntakeDataGrid/IntakeDataGrid.hooks';
import { SupplierContactDataOptional } from '../../components/SupplierContactForm/SupplierContactForm.schema';
import { IntakeSupplierContactQuery } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';

import { apiToSupplier, gridDataToSupplier } from './IntakeQuoteSupplierConfirmationStep.util';

type UseNextMutationProps = {
  apiRef: MutableRefObject<GridApi>;
  session: any;
  next: (options: { variables: { data: any } }) => void;
  isLoading: boolean;
  schema: ZodArray<ZodType<SupplierContactDataOptional>>;
};

export const useNextMutation = ({ apiRef, session, next, isLoading, schema }: UseNextMutationProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeQuoteSupplierConfirmationStep' });
  const { onError } = useCustomSnackbar();

  const { parse } = useGridValidator({
    apiRef,
    schema,
  });

  const handleNext = useCallback(async () => {
    // Given the injected confirmation `schema` (min(1).max(1)) there must be exactly 1 entry
    // otherwise parse will throw an exception and the UI will break
    // `isValid` from `useGridValidator` would allow more graceful handling
    try {
      const row = parse()[0];
      await next({
        variables: {
          data: {
            ...gridDataToSupplier(row),
            chatSessionId: session.id,
          },
        },
      });
    } catch (error) {
      onError(t('error.supplier'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, parse, session.id]);
  return { handleNext, isLoading };
};

type UseSupplierResult = {
  quotedSupplier: SupplierContactDataOptional[];
  otherSupplierIds: string[];
};

export const useSupplierData = (data: IntakeSupplierContactQuery | undefined): UseSupplierResult => {
  return useMemo(() => {
    if (!data?.sevenbridges.chatSession.suppliers) {
      return { quotedSupplier: [], otherSupplierIds: [] };
    }

    return data.sevenbridges.chatSession.suppliers.reduce<UseSupplierResult>(
      (acc, supplier) => {
        const transformedSupplier = apiToSupplier(supplier);
        if (supplier.isQuoted) {
          acc.quotedSupplier.push(transformedSupplier);
        } else {
          acc.otherSupplierIds.push(transformedSupplier.supplier.internalId);
        }
        return acc;
      },
      { quotedSupplier: [], otherSupplierIds: [] },
    );
  }, [data]);
};
